/**
 * Peer-identity helpers for the agent IM tabs (Telegram / WhatsApp).
 *
 * The email tab already turns a bare address into a curated contact name
 * (`mail-address.ts` + `useContactNames`). The IM tabs never got the same join,
 * so a stranger renders as `telegram:<chatid>` instead of the name the lawyer
 * gave them. These pure helpers are the peer-keyed twin of that machinery:
 *
 *   - `peerKey` — the ONE join-key builder, used by both the index builder and
 *     the resolver so they cannot drift on normalization;
 *   - `buildContactPeerNameIndex` — `(channel, peerId) → curated identity`, the
 *     peer analogue of `buildContactNameIndex`;
 *   - `contactInitials` — the fallback glyph for the initials avatar.
 *
 * Normalization rule (load-bearing): the CHANNEL is lowercased on both sides
 * (it is a small closed vocabulary — "telegram"/"whatsapp"); the PEER ID is
 * trimmed but NEVER lowercased. Peer ids are opaque channel-native tokens (a
 * chat id, a WhatsApp JID, a phone number), and the runtime authority compares
 * them verbatim (`extensions/lexiun-core/src/contacts/resolve-contact.ts` uses
 * `ch.peerId === query.peerId` with no case folding, unlike its email branch
 * which lowercases). Lowercasing a JID here would silently miss the binding.
 */
import type { TrustTier } from "@/types/contacts";

/** A curated identity for one IM peer, resolved from the contacts store. */
export interface PeerContact {
  /** The name the lawyer gave this correspondent. Always non-empty. */
  name: string;
  /** §4.1 trust tier — drives the row/thread badge. */
  tier?: TrustTier;
  /** §2 relationship slug (free vocabulary), carried for a future badge. */
  relationship?: string;
}

/** The single (channel, peerId) → index-key builder. Channel case-folded,
 *  peerId trim-only (opaque token; the runtime compares it verbatim). */
export function peerKey(channel: string, peerId: string): string {
  return `${channel.trim().toLowerCase()}:${peerId.trim()}`;
}

/** The minimal contact shape this index reads — structural on purpose (a bare
 *  literal satisfies it) so tests need not build a whole `Contact`. */
interface IndexableContact {
  displayName?: string;
  relationship?: string;
  trustTier?: TrustTier;
  channels?: { channel: string; peerId: string }[];
}

/**
 * Builds a `(channel, peerId) → PeerContact` index from the contacts store.
 *
 * Mirrors `buildContactNameIndex` (the email side): a contact with no usable
 * name contributes NOTHING (never masks the raw peer with an empty string), and
 * first-writer-wins so a peer bound to two contacts resolves deterministically
 * by the store's stable order rather than by luck.
 */
export function buildContactPeerNameIndex(
  contacts: IndexableContact[],
): Map<string, PeerContact> {
  const index = new Map<string, PeerContact>();
  for (const c of contacts) {
    const name = c.displayName?.trim();
    if (!name) continue;
    const entry: PeerContact = {
      name,
      ...(c.trustTier ? { tier: c.trustTier } : {}),
      ...(c.relationship ? { relationship: c.relationship } : {}),
    };
    for (const ch of c.channels ?? []) {
      if (!ch?.channel?.trim() || !ch?.peerId?.trim()) continue;
      const key = peerKey(ch.channel, ch.peerId);
      if (!index.has(key)) index.set(key, entry);
    }
  }
  return index;
}

/** Leading honorific tokens ("Dr.", "Dra.", "Sra.", "Prof.") the initials skip
 *  so the glyph comes from the name, not the title. */
const HONORIFIC = /^(dr|dra|sr|sra|srta|prof|profa|exmo|exma|doutor|doutora)\.?$/i;

/**
 * Up to two uppercase initials for an initials avatar.
 *
 * Strips a single leading honorific, then takes the first CODE POINT (accent-
 * and emoji-safe, unlike `charAt`) of the first two remaining words. A single
 * word yields one letter; a bare numeric peer yields its first digit; an empty
 * or whitespace label yields "" (the caller renders a neutral placeholder).
 */
export function contactInitials(label: string): string {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1 && HONORIFIC.test(words[0]!)) words.shift();
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? "")
    .join("")
    .toUpperCase();
}
